/*
 * SYLTBAR — Wine Club cart controller
 * ==========================================================================
 * One script, one cart read. Handles both cart surfaces (drawer + cart page):
 *
 *   1. CHECKOUT GATE — a Wine Club box must hold EXACTLY one of the case sizes
 *      configured in Skio (12 / 24 / 36 / 48 / 60). 13 bottles is not a case, so
 *      checkout is disabled until the shopper reaches the next size. The shopper-facing
 *      message lives at the TOP of the cart and is rendered by cart_ajax.liquid,
 *      which re-renders on every cart change — this script only disables the
 *      button. Normal / one-time products are ignored: they never count toward
 *      the box and never rescue an incomplete one.
 *
 *   2. "WELCOME TO THE CLUB!" PANEL — shows the live discount and a frequency
 *      dropdown built from the REAL Skio selling plans, pre-selected to the
 *      plan the cart is currently on. Changing it re-maps every box line.
 *
 * Why the panel is JS-driven:
 *   cart-form.js only swaps [data-products] / [data-discounts] / [data-subtotal]
 *   on a cart change — the drawer FOOTER (where the panel lives) is left alone.
 *   So Liquid renders the correct state on page load, and this script keeps it
 *   accurate afterwards from the cart payload the theme already gives us.
 *
 * Request budget:
 *   - cart:updated carries the full cart -> 0 extra requests.
 *   - other cart events -> one debounced /cart.js, shared by gate + panel.
 *   - Skio plan list -> at most once per session (builder -> sessionStorage -> network).
 *   - changing frequency -> 2 calls (remove + re-add); Shopify cannot swap a
 *     selling_plan in place.
 */
(function () {
  'use strict';

  var MIN_BOTTLES = 12;
  var SKIO_ENDPOINT = 'https://api.skio.com/storefront-http/get-dynamic-box';
  var PRODUCT_PLATFORM_ID = '7472220176439';
  var CACHE_KEY = 'syltbar_wc_box_v2';
  var TAG = '[WineClub]';

  var SEL = {
    checkout: '.cart__checkout',
    panel: '[data-wine-club-freq]',
    freqRow: '[data-wine-club-freq-row]',
    freqControl: '[data-wine-club-freq-control]',
    freqSelect: '[data-wine-club-freq-select]',
    perks: '[data-wine-club-perks]',
    title: '[data-wine-club-title]',
    minimum: '[data-wine-club-minimum]',
    subtotalCompare: '[data-subtotal-compare]',
    boxRow: '.cart__item--wine-club',
    plusBtn: '.cart__wine-club-group .js-qty__adjust--plus',
    qtyInput: '.cart__wine-club-group .js-qty__num'
  };

  var cartState = { hasBox: false, bottles: 0, planId: null, discount: 0, original: 0, total: 0, originalTotal: 0, caseSize: 0 };
  var tiers = null;   // [{ size, kind, value }] from Skio — case sizes + their discount
  var freqPromise = null;
  var fetchTimer = null;
  var busy = false;

  /* ================= helpers ================= */
  function numericId(id) { return String(id == null ? '' : id).split('/').pop(); }
  function $all(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }

  function intervalLabel(interval, count) {
    var unit = { DAY: 'Day', WEEK: 'Week', MONTH: 'Month', YEAR: 'Year' }[String(interval || '').toUpperCase()] || 'Delivery';
    count = Number(count) || 1;
    return 'Every ' + count + ' ' + unit + (count > 1 ? 's' : '');
  }

  // Per-variant plan, falling back to the group plan (same rule as build-a-box.js).
  function resolvePlan(freq, variantId) {
    if (!freq) return null;
    var map = freq.variantMapping || {};
    var entry = map[String(variantId)] != null ? map[String(variantId)] : map[numericId(variantId)];
    if (entry == null) return freq.groupPlanId || null;
    if (typeof entry === 'string' || typeof entry === 'number') return entry;
    return entry.planId || entry.sellingPlanId || entry.id || freq.groupPlanId || null;
  }

  /* ================= read the cart ================= */
  function evaluate(cart) {
    var bottles = 0, original = 0, discount = 0, planId = null, hasBox = false;

    (cart.items || []).forEach(function (it) {
      if (!it.properties || it.properties._dynamicBoxIndex == null) return;
      hasBox = true;
      bottles += it.quantity;
      original += (it.original_line_price != null ? it.original_line_price : it.line_price);
      discount += (it.line_level_total_discount || 0);
      if (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan) {
        planId = it.selling_plan_allocation.selling_plan.id;
      }
    });

    cartState.hasBox = hasBox;
    cartState.bottles = bottles;
    cartState.planId = planId;
    cartState.discount = discount;
    cartState.original = original;
    cartState.total = cart.total_price || 0;
    cartState.originalTotal = cart.original_total_price || 0;
    // The case the shopper is filling, published by build-a-box.js. The cart holds
    // the last COMPLETE case, so without this the "+" stayed locked at 12 after
    // they moved up to case 24.
    cartState.caseSize = Number((cart.attributes && cart.attributes._wc_case) || 0) || 0;
  }

  /* ---------- case sizes + tier discount (from Skio, never hardcoded) ---------- */
  function tierSizes() {
    return (tiers || []).map(function (t) { return t.size; }).sort(function (a, b) { return a - b; });
  }

  // Percent for an EXACT case size. Skio may store 15 or 0.15 — both normalise.
  function tierPercent(size) {
    var t = (tiers || []).filter(function (x) { return x.size === size; })[0];
    if (!t || t.kind !== 'percent' || t.value == null) return 0;
    var v = Number(t.value);
    if (isNaN(v)) return 0;
    if (v > 0 && v < 1) v = v * 100;
    return Math.round(v);
  }

  /* The discount a Wine Club box is entitled to right now.
     Exact case -> that tier. In between (e.g. 13) -> the case already earned
     (12), so the member price never disappears mid-edit. Below the first case
     -> the first case, so the promise stays visible. */
  function earnedPercent(count) {
    var sizes = tierSizes();
    if (!sizes.length) return 0;
    var earned = sizes[0];
    for (var i = 0; i < sizes.length; i++) if (sizes[i] <= count) earned = sizes[i];
    return tierPercent(earned);
  }

  function isValidCase(count) {
    var sizes = tierSizes();
    if (!sizes.length) return count >= MIN_BOTTLES;   // config missing -> old rule
    return sizes.indexOf(count) > -1;
  }

  function nextCaseSize(count) {
    var sizes = tierSizes();
    for (var i = 0; i < sizes.length; i++) if (sizes[i] > count) return sizes[i];
    return null;                                      // already past the biggest case
  }

  // The discount actually shown: prefer what the cart reports, fall back to the tier.
  function effectiveDiscount() {
    if (cartState.discount > 0) return cartState.discount;
    var pct = tierPercent(cartState.bottles);
    if (!pct || !cartState.original) return 0;
    return Math.round(cartState.original * pct / 100);
  }

  /* ================= 1. checkout gate ================= */
  function applyGate() {
    var blocked = cartState.hasBox && !isValidCase(cartState.bottles);

    $all(SEL.checkout).forEach(function (btn) {
      if (!blocked && btn.dataset.wcTouched !== '1') return;   // never ours -> don't touch
      btn.disabled = blocked;
      btn.classList.toggle('is-disabled', blocked);
      btn.setAttribute('aria-disabled', String(blocked));
      if (blocked) btn.dataset.wcTouched = '1'; else delete btn.dataset.wcTouched;
    });
  }

  /* ---------- top-of-cart note + per-line pricing ----------
     cart_ajax.liquid paints these server-side from the real cart. When Skio has
     not allocated the discount to the cart lines yet, we fill the gap from the
     tier config so the shopper always sees the case price they were promised. */
  function noteEl() {
    // Preferred hook, then the classes the older cart_ajax markup uses.
    return document.querySelector('[data-wc-note]') ||
           document.querySelector('.cart__minimum_note') ||
           document.querySelector('.cart__member-savings');
  }

  function applyNote() {
    var el = noteEl();
    if (!el) return;

    if (!cartState.hasBox) { el.hidden = true; el.textContent = ''; return; }

    el.hidden = false;

    if (!isValidCase(cartState.bottles)) {
      var next = nextCaseSize(cartState.bottles);
      el.className = 'cart__minimum_note cart__member-savings';
      el.innerHTML = next
        ? 'Add <b>' + (next - cartState.bottles) + '</b> more — a Wine Club case is ' + tierSizes().join(', ') + ' bottles'
        : 'Remove <b>' + (cartState.bottles - tierSizes().pop()) + '</b> — the largest case is ' + tierSizes().pop() + ' bottles';
      return;
    }

    var saved = effectiveDiscount();
    el.className = 'cart__member-savings';
    el.innerHTML = saved > 0 ? 'Being a member saves you <b>' + formatMoney(saved) + '</b> today' : 'Wine Club case unlocked';
  }

  function formatMoney(cents) {
    try {
      if (window.theme && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.moneyFormat || (theme.settings && theme.settings.moneyFormat));
      }
    } catch (e) {}
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  /* Per-line price. Only steps in when the cart itself reports no discount. */
  /* Per-line price for Wine Club rows.
     - Shopify already allocated the discount -> leave Liquid's markup alone.
     - It has not (mid-edit, or the count is between cases) -> paint the member
       price from the earned tier so the discount is ALWAYS visible.
     - Nothing to work with -> leave the DOM untouched rather than blanking it. */
  function applyLinePrices() {
    var rows = $all('[data-wc-price]');
    if (!rows.length) return;
    if (cartState.discount > 0) return;              // real discount already shown

    var pct = earnedPercent(cartState.bottles);
    if (!pct) return;                                // don't wipe what Liquid rendered

    rows.forEach(function (row) {
      var original = Number(row.getAttribute('data-original') || 0);
      if (!original) return;
      var target = row.querySelector('.cart__price');
      if (!target) return;

      var discounted = Math.round(original * (100 - pct) / 100);
      target.innerHTML = formatMoney(discounted) +
        '<span class="cart__price cart__price--strikethrough">' + formatMoney(original) + '</span>';
    });
  }

  /* ---------- case size lock ----------
     The box may not grow past the case the shopper selected. In the cart that
     means: once the bottle count sits exactly on a case size (12 / 24 / 36 …),
     the "+" on every Wine Club line is switched off. To add more they change the
     case size on the Build-a-Box page — which is what the Edit link is for. */
  /* The "+" is locked only when the case being filled is actually full.
     Target = the case the builder published; fall back to "the count is exactly a
     case size" when no attribute is present (older carts, or the box was built
     before this shipped).
     NOTE: this drives the PLUS BUTTON only. Checkout eligibility stays on
     isValidCase() — a cart holding a complete 12 can still be bought even while
     the shopper is part-way through filling a 24. */
  function caseTarget() {
    if (cartState.caseSize) return cartState.caseSize;
    if (isValidCase(cartState.bottles)) return cartState.bottles;
    return nextCaseSize(cartState.bottles) || 0;
  }

  function caseIsFull() {
    if (!cartState.hasBox) return false;
    var target = caseTarget();
    return target > 0 && cartState.bottles >= target;
  }

  /* The lock lives on <body>, not on the buttons.
     cart-form.js replaces the whole [data-products] markup on every update, so
     anything set on a button is gone the moment the cart redraws — that is what
     let the limit escape after a minus/plus. <body> is never re-rendered, so one
     class there keeps every current AND future "+" locked, with no timers and
     nothing to repaint. */
  function applyCaseLock() {
    document.body.classList.toggle('wc-case-full', caseIsFull());
  }

  /* ---------- the actual enforcement ----------
     Disabling the button is only cosmetic: cart-form.js replaces the whole
     [data-products] markup on every update, so the fresh "+" comes back enabled
     before applyCaseLock() can run again. That is how the limit escaped —
     decrement to 11, increment back, and from 12 onwards nothing stopped you.
     The rule is therefore enforced on the click itself, in the capture phase,
     before the theme's own handler sees it. No repaint required. */
  document.addEventListener('click', function (e) {
    var plus = e.target.closest ? e.target.closest(SEL.plusBtn) : null;
    if (!plus) return;
    if (!plus.closest(SEL.boxRow)) return;      // only Wine Club lines

    // First paint on /cart happens before the first cart read returns (~200ms).
    // Until we know the bottle count we cannot say whether the case is full, so
    // hold the click, read the cart, and let the shopper click again.
    if (!cartReady) { e.preventDefault(); e.stopImmediatePropagation(); refresh(); return; }

    if (!caseIsFull()) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    plus.classList.add('is-disabled');
    plus.title = 'Change your case size to add more bottles';
  }, true);

  /* Remember what a quantity field held before it was edited. */
  document.addEventListener('focusin', function (e) {
    var input = e.target.closest ? e.target.closest(SEL.qtyInput) : null;
    if (input && input.closest(SEL.boxRow)) input.dataset.wcWas = input.value;
  });

  /* Typing a bigger number bypasses the buttons, so clamp that too. */
  document.addEventListener('change', function (e) {
    var input = e.target.closest ? e.target.closest(SEL.qtyInput) : null;
    if (!input) return;
    var row = input.closest(SEL.boxRow);
    if (!row) return;

    var was = Number(input.dataset.wcWas || input.dataset.wcMax || 0);
    var now = Number(input.value || 0);
    if (now <= was) return;                          // reducing is always fine

    // Room left in the case the box is currently working toward.
    var target = caseTarget();
    var headroom = target ? Math.max(0, target - cartState.bottles) : 0;

    if (now - was > headroom) {
      input.value = was + headroom;                  // clamp to the selected case
      input.dataset.wcWas = input.value;
    }
  }, true);

  /* ---------- subtotal: show what the case saved ----------
     The compare price lives in a SIBLING of [data-subtotal], never inside it:
     cart-form.js does `this.subtotal.innerHTML = formatMoney(...)` on every cart
     update, so anything placed inside that element gets wiped. Liquid renders
     this span server-side and we only update its text/visibility. */
  function applySubtotal() {
    var els = $all(SEL.subtotalCompare);
    if (!els.length) return;

    // Whatever the lines are showing, the subtotal has to agree with it.
    var compare = 0;
    if (cartState.originalTotal > cartState.total) {
      compare = cartState.originalTotal;                       // real cart discount
    } else if (cartState.hasBox) {
      var pct = earnedPercent(cartState.bottles);              // member price we painted
      if (pct) compare = cartState.total + Math.round(cartState.original * pct / 100);
    }

    els.forEach(function (el) {
      if (compare > cartState.total) {
        el.textContent = formatMoney(compare);
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    });
  }

  /* ================= 2. frequency panel ================= */
  function loadFrequencies() {
    if (freqPromise) return freqPromise;

    // (a) the builder already computed them on the Wine Club page
    if (window.SKIO_BAB_FREQUENCIES && window.SKIO_BAB_FREQUENCIES.length) {
      if (window.SKIO_BAB_TIERS) tiers = window.SKIO_BAB_TIERS;
      freqPromise = Promise.resolve(window.SKIO_BAB_FREQUENCIES);
      return freqPromise;
    }

    // (b) session cache
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.freqs && cached.freqs.length) {
        tiers = cached.tiers || null;
        freqPromise = Promise.resolve(cached.freqs);
        return freqPromise;
      }
    } catch (e) {}

    // (c) network, once
    freqPromise = fetch(SKIO_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productPlatformId: String(PRODUCT_PLATFORM_ID) })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var box = (data && data.DynamicBox) ? data.DynamicBox : data;

        // case sizes + their discount
        var tierMap = (box && (box.percentDiscountTier || box.fixedDiscountTier)) || {};
        var kind = (box && box.percentDiscountTier) ? 'percent' : 'fixed';
        tiers = Object.keys(tierMap)
          .map(Number).filter(function (n) { return !isNaN(n); })
          .sort(function (a, b) { return a - b; })
          .map(function (size) { return { size: size, kind: kind, value: tierMap[size] }; });

        var groups = (box && box.sellingPlanGroup) || {};
        var weight = { DAY: 1, WEEK: 7, MONTH: 30, YEAR: 365 };
        var freqs = Object.keys(groups).map(function (groupPlanId) {
          var g = groups[groupPlanId] || {};
          return {
            groupPlanId: groupPlanId,
            interval: g.interval,
            intervalCount: g.intervalCount,
            variantMapping: g.variantMapping || {},
            label: intervalLabel(g.interval, g.intervalCount)
          };
        }).sort(function (a, b) {
          return (weight[String(a.interval).toUpperCase()] || 30) * (a.intervalCount || 1) -
                 (weight[String(b.interval).toUpperCase()] || 30) * (b.intervalCount || 1);
        });
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ freqs: freqs, tiers: tiers })); } catch (e) {}
        return freqs;
      })
      .catch(function (err) {
        console.warn(TAG, 'could not load frequencies:', err);
        freqPromise = null;              // allow a retry next time the cart opens
        return [];
      });

    return freqPromise;
  }

  function indexOfPlan(freqs, planId) {
    if (!planId) return -1;
    var want = String(numericId(planId));
    for (var i = 0; i < freqs.length; i++) {
      var f = freqs[i];
      if (String(numericId(f.groupPlanId)) === want) return i;
      var map = f.variantMapping || {};
      var keys = Object.keys(map);
      for (var j = 0; j < keys.length; j++) {
        var e = map[keys[j]];
        var pid = (e && (e.planId || e.sellingPlanId || e.id)) || e;
        if (String(numericId(pid)) === want) return i;
      }
    }
    return -1;
  }

  function applyPanel() {
    var panels = $all(SEL.panel);
    if (!panels.length) return;

    var unlocked = isValidCase(cartState.bottles);
    var next = nextCaseSize(cartState.bottles);
    // Locked shows the discount being worked TOWARDS (the next case);
    // unlocked shows the one actually earned.
    var pct = unlocked
      ? tierPercent(cartState.bottles)
      : tierPercent(next || tierSizes()[0]);

    panels.forEach(function (panel) {
      // hidden AND inline display, so no stylesheet can leave it on screen when
      // the cart holds no Wine Club box (the panel was showing on plain carts).
      panel.hidden = !cartState.hasBox;
      panel.style.display = cartState.hasBox ? '' : 'none';
      if (!cartState.hasBox) return;

      panel.classList.toggle('is-locked', !unlocked);
      panel.classList.toggle('is-unlocked', unlocked);

      var title = panel.querySelector(SEL.title);
      if (title) title.textContent = unlocked ? 'Welcome To The Club!' : 'Join the Wine Club';

      var perks = panel.querySelector(SEL.perks);
      if (perks) {
        var lead = unlocked ? 'Enjoy ' : 'Get ';
        perks.textContent = pct > 0 ? lead + pct + '% off + Free shipping' : 'Free shipping';
      }

      var minimum = panel.querySelector(SEL.minimum);
      if (minimum) {
        minimum.hidden = unlocked;
        if (!unlocked && next) minimum.textContent = next + ' bottle minimum to unlock wine club';
      }

      var row = panel.querySelector(SEL.freqRow);
      if (row) row.hidden = !unlocked;
    });

    if (!cartState.hasBox || !unlocked) return;      // no dropdown while locked

    loadFrequencies().then(function (freqs) {
      if (!freqs.length) return;
      var selected = indexOfPlan(freqs, cartState.planId);
      if (selected < 0) selected = 0;

      $all(SEL.panel).forEach(function (panel) {
        if (panel.hidden) return;
        panel._freqs = freqs;
        panel._selected = selected;

        var control = panel.querySelector(SEL.freqControl);
        if (!control) return;

        if (panel._built) {                          // options exist -> keep selection in step
          var existing = panel.querySelector(SEL.freqSelect);
          if (existing && !busy) existing.value = String(selected);
          return;
        }

        control.innerHTML = '<select class="wc-club-panel__select" data-wine-club-freq-select>' +
          freqs.map(function (f, i) {
            return '<option value="' + i + '"' + (i === selected ? ' selected' : '') + '>' + f.label + '</option>';
          }).join('') +
          '</select>';
        panel._built = true;
      });
    });
  }

  /* ---------- subtotal: show what the case saved ----------
     cart-form.js writes a plain subtotal, so the original is appended as a
     strikethrough afterwards (we run on cart:updated, which it fires last). */

  /* Re-map the selling plan on every box line.
     Reads the cart fresh so the line keys can never be stale. */
  function changeFrequency(panel, index) {
    if (busy) return;
    var freq = (panel._freqs || [])[index];
    if (!freq) return;

    busy = true;
    panel.classList.add('is-busy');
    var select = panel.querySelector(SEL.freqSelect);
    if (select) select.disabled = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var lines = (cart.items || []).filter(function (it) {
          return it.properties && it.properties._dynamicBoxIndex != null;
        });
        if (!lines.length) throw new Error('no box lines');

        var boxIndex = lines[0].properties._dynamicBoxIndex;
        var boxId = lines[0].properties._dynamicBoxId;

        var updates = {};
        lines.forEach(function (l) { updates[l.key] = 0; });

        var items = lines.map(function (l) {
          var props = { _dynamicBoxIndex: boxIndex };
          if (boxId != null) props._dynamicBoxId = boxId;
          var item = { id: l.variant_id, quantity: l.quantity, properties: props };
          var planId = resolvePlan(freq, l.variant_id);
          if (planId) item.selling_plan = Number(numericId(planId));
          return item;
        });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (r) {
          if (!r.ok) throw new Error('remove failed');
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items })
          });
        });
      })
      .then(function (r) {
        if (!r.ok) throw new Error('re-add failed');
        panel._selected = index;
        document.dispatchEvent(new CustomEvent('cart:build'));   // redraw items + totals
        refresh();                                               // refresh gate + panel
      })
      .catch(function (err) {
        console.warn(TAG, 'frequency change failed:', err);
        if (select) select.value = String(panel._selected);      // roll the UI back
      })
      .finally(function () {
        busy = false;
        panel.classList.remove('is-busy');
        if (select) select.disabled = false;
      });
  }

  /* ================= shared refresh ================= */
  /* ---------- hard scope guard ----------
     Nothing below is allowed to touch a cart that has no Wine Club box. On a
     plain cart every painter is reset to a no-op and the checkout button is
     handed back to the theme untouched. */
  function releaseNormalCart() {
    $all(SEL.checkout).forEach(function (btn) {
      if (btn.dataset.wcTouched !== '1') return;   // we never disabled it -> leave alone
      btn.disabled = false;
      btn.classList.remove('is-disabled');
      btn.removeAttribute('aria-disabled');
      delete btn.dataset.wcTouched;
    });
    $all(SEL.panel).forEach(function (p) { p.hidden = true; p.style.display = 'none'; });
  }

  function paint() {
    // Tiers decide both the gate and the pricing, so make sure they are loaded.
    if (cartState.hasBox && !tiers) {
      loadFrequencies().then(function () {
        applyGate();
        // applyNote();
        applyLinePrices();
        applySubtotal();
        applyCaseLock();
        applyPanel();
      });
      return;
    }
    if (!cartState.hasBox) {          // plain cart -> behave as if this file is not loaded
      releaseNormalCart();
      applySubtotal();                // only reflects a REAL Shopify discount
      return;
    }

    applyGate();
    // applyNote();
    applyLinePrices();
    applySubtotal();
    applyCaseLock();
    applyPanel();
  }

  var lastCartAt = 0;
  var cartReady = false;      // true once the bottle count is known

  function refresh(cart) {
    if (cart && cart.items) {          // handed to us -> zero network
      lastCartAt = Date.now();
      cartReady = true;
      evaluate(cart);
      paint();
      return;
    }
    // cart:build fires right after cart:updated for the same change. If we were
    // just given the cart, don't turn around and fetch the very same thing.
    if (Date.now() - lastCartAt < 600) { paint(); return; }
    clearTimeout(fetchTimer);
    fetchTimer = setTimeout(function () {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { lastCartAt = Date.now(); cartReady = true; evaluate(c); paint(); })
        .catch(function () { /* keep the last known state */ });
    }, 150);
  }

  /* ================= wiring ================= */
  // Delegated: survives every drawer re-render.
  document.addEventListener('change', function (e) {
    var select = e.target.closest ? e.target.closest(SEL.freqSelect) : null;
    if (!select) return;
    var panel = select.closest(SEL.panel);
    if (panel) changeFrequency(panel, Number(select.value));
  });


  function boot() {
    document.addEventListener('cart:updated', function (e) {
      refresh(e && e.detail && e.detail.cart);   // theme hands us the cart -> no fetch
    });
    ['cart:build', 'ajaxProductAdded', 'skio:box-added', 'CartDrawer:open', 'drawerOpen'].forEach(function (evt) {
      document.addEventListener(evt, function () { refresh(); });
    });
    refresh();
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();